import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Card, Space, Table, Typography } from 'antd';
import api from '../../services/api';

const { Title, Text } = Typography;

/**
 * What the shop requires of a customer, per stage, editable by the owner.
 *
 * Route: /owner/settings/requirements (owner-only; mounted under owner routes,
 * because `PUT /api/admin/completeness/policy` answers 403 to anyone who is not
 * the owner and a door that always fails is worse than no door).
 *
 * Spec §10.1 makes this a same-release obligation: the policy ships strict and
 * ON by default (D13/D14), so a default that is wrong for this shop must be
 * fixable here rather than by a developer. Without this screen a configuration
 * mistake becomes an outage — approvals stall and nobody in the shop can lift
 * the block.
 *
 * The field list comes from the server's policy for the stage. This screen never
 * decides what fields exist.
 */

const STAGES = [
  { value: 'approval', label: 'Customer approval' },
  { value: 'quote_request', label: 'Quote request' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'work_order', label: 'Work order' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'payment', label: 'Payment' },
];

const LEVELS = ['optional', 'suggested', 'required', 'mandatory'] as const;
type Level = (typeof LEVELS)[number];

/**
 * Mirrors the server's LABELS map (CustomerCompletenessService). A raw column
 * name must never reach the owner.
 *
 * One deliberate divergence: the server labels `preferred_contact_channel` as
 * "A way to reach you matching your contact preference" because that string is
 * read by CUSTOMERS. On this screen the reader is the owner configuring policy,
 * so the shorter administrative name is the clearer one.
 */
const LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  phone: 'Phone number',
  email: 'Email address',
  address: 'Street address',
  address_line2: 'Apt / Suite / Unit',
  city: 'City',
  state: 'State / Province',
  zip: 'ZIP / Postal code',
  country: 'Country',
  preferred_contact_channel: 'Preferred contact channel',
  make: 'Vehicle make',
  model: 'Vehicle model',
  year: 'Vehicle year',
  vin: 'VIN',
  license_plate: 'License plate',
  current_mileage: 'Current mileage',
};

const labelFor = (field: string) => LABELS[field] ?? field;

const LEVEL_HELP: Record<Level, string> = {
  optional: 'Not asked for.',
  suggested: 'Asked for and encouraged — never blocks anything.',
  required: 'The customer must provide it to approve. Staff can waive it per estimate, and the waiver is recorded.',
  mandatory: 'The customer must provide it, and no one can waive it — not even you.',
};

function RequirementPolicyPageInner(): React.ReactElement {
  // antd's STATIC `message` logs "Static function can not consume context like
  // dynamic theme" — seen live in the browser console on the first save. Same
  // App.useApp() pattern as PromoCodesPage, the neighbouring settings screen.
  const { message } = App.useApp();
  const [stage, setStage] = useState<string>('approval');
  const [levels, setLevels] = useState<Record<string, Level>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnMandatory, setWarnMandatory] = useState(false);

  const load = useCallback(async (which: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/completeness/policy?stage=${encodeURIComponent(which)}`);
      setLevels(res.data?.levels ?? {});
    } catch {
      setError('Could not load the current policy.');
      setLevels({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(stage);
  }, [stage, load]);

  const setLevel = (field: string, level: Level) => {
    setLevels((prev) => ({ ...prev, [field]: level }));
    if (level === 'mandatory') setWarnMandatory(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put('/completeness/policy', { stage, levels });
      message.success('Requirements saved');
    } catch (e: any) {
      // Never claim success on a failed write. A silently-unsaved policy is
      // worse than a visible error: the owner walks away believing it changed.
      // Both branches open with "Could not save" so the headline is the same
      // regardless of cause; only the cause differs.
      setError(e?.response?.data?.error === 'forbidden_policy_write'
        ? 'Could not save — only the shop owner can change these requirements.'
        : 'Could not save the requirements. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  const rows = Object.keys(levels)
    .sort((a, b) => labelFor(a).localeCompare(labelFor(b)))
    .map((field) => ({ key: field, field }));

  return (
    <Card>
      <Title level={4}>Customer information requirements</Title>
      <Text type="secondary">
        What the shop asks a customer for, and how hard. Changes apply to new approvals
        immediately.
      </Text>

      <div style={{ margin: '16px 0' }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          Stage
        </Text>
        <select
          data-testid="stage-select"
          aria-label="Stage"
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          style={{ fontSize: 16, padding: 8, minWidth: 220, minHeight: 44 }}
        >
          {STAGES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {warnMandatory && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Mandatory cannot be waived"
          description="A mandatory field blocks approval for everyone — not even you can waive it for a single estimate. Use Required unless you are certain."
          closable
          onClose={() => setWarnMandatory(false)}
        />
      )}

      <Table
        loading={loading}
        dataSource={rows}
        pagination={false}
        size="small"
        columns={[
          {
            key: 'field',
            title: 'Field',
            dataIndex: 'field',
            render: (field: string) => <Text>{labelFor(field)}</Text>,
          },
          {
            key: 'level',
            title: 'How hard we ask',
            dataIndex: 'field',
            render: (field: string) => (
              <select
                data-testid={`level-${field}`}
                aria-label={`${labelFor(field)} requirement level`}
                value={levels[field]}
                onChange={(e) => setLevel(field, e.target.value as Level)}
                style={{ fontSize: 16, padding: 6, minWidth: 150, minHeight: 44 }}
              >
                {LEVELS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            ),
          },
        ]}
      />

      <Space direction="vertical" style={{ marginTop: 16, width: '100%' }} size="small">
        {LEVELS.map((l) => (
          <Text key={l} type="secondary" style={{ fontSize: 12 }}>
            <strong>{l}</strong> — {LEVEL_HELP[l]}
          </Text>
        ))}
        <Button
          type="primary"
          size="large"
          loading={saving}
          onClick={save}
          data-testid="save-policy"
        >
          Save requirements
        </Button>
      </Space>
    </Card>
  );
}

export default function RequirementPolicyPage(): React.ReactElement {
  return (
    <App>
      <RequirementPolicyPageInner />
    </App>
  );
}
